import * as React from "react";
import { motion, useReducedMotion } from "motion/react";
import { useQueryClient, useQueries } from "@tanstack/react-query";
import {
  BookOpen,
  Brain,
  Bell,
  History,
  Loader2,
  PenLine,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import { ApiError } from "@eduviaos/shared";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
} from "@eduviaos/ui-kit";
import { Reveal, SectionCard, SectionHeader } from "../../components/Section";
import { ResponsiveSheetContent } from "../../components/ResponsiveSheetContent";
import { PlanPicker } from "../../components/sc/PlanPicker";
import { QuestionCountPicker } from "../../components/sc/QuestionCountPicker";
import { clampQuestionCount } from "../../lib/learning/questionCount";
import { TrackRecordDetailSheet } from "../../components/sc/TrackRecordDetailSheet";
import { Ziggy } from "../../components/Ziggy";
import { LearnPageHeader } from "../../components/sc/LearnPageHeader";
import { MotivationBanner } from "../../components/sc/MotivationBanner";
import { PathwayProgressCard } from "../../components/sc/PathwayProgressCard";
import { QuickActionGrid, type QuickActionItem } from "../../components/sc/QuickActionGrid";
import { QuizPromoCard } from "../../components/sc/QuizPromoCard";
import { useSession } from "../../lib/sessionContext";
import type { ZiggyAge } from "../../lib/ziggy";
import { useLearnLocale } from "../../lib/i18n/useLearnLocale";
import { intlLocale } from "../../lib/i18n/intl";
import type { PortalLocale } from "../../lib/i18n/portalShell";
import { isModuleDisabledError } from "../../lib/learning/errors";
import {
  COACH_KEY,
  useCoachSummary,
  useDueCards,
  useGenerateProgressReport,
  useLearningPreferences,
  useLogReading,
  useMarkNotificationRead,
  useNotifications,
  usePathways,
  useProgressReports,
  useStartDiagnostic,
  useStartPracticeTest,
  useTrackRecord,
  useCourseNavigation,
} from "../../lib/learning/hooks";
import { isReadForPleasurePlan } from "../../lib/learning/copy";
import { useSelectedPathway } from "../../lib/learning/useSelectedPathway";
import { useMyEnrollment, MY_ENROLLMENT_KEY } from "../../lib/enrollment/api";
import { coachApi } from "../../lib/learning/api";
import type {
  CourseLeaf,
  DiagnosticSession,
  LearnHubAction,
  Pathway,
  PracticeTestSession,
  ProgressReport,
  SkillAssessmentSession,
  TrackRecordEntry,
} from "../../lib/learning/types";
import { IntakeWizard } from "./IntakeWizard";
import { PlanRoadmapPage } from "./PlanRoadmapPage";
import { DiagnosticRunner } from "./DiagnosticRunner";
import { PracticeTestRunner } from "./PracticeTestRunner";
import { ReviewSession } from "./ReviewSession";
import { SkillAssessmentRunner } from "./SkillAssessmentRunner";
import { SkillAssessmentPanel } from "./SkillAssessmentPanel";
import { AppPaywall } from "./AppPaywall";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

type View = "hub" | "intake" | "roadmap" | "diagnostic" | "review" | "practice" | "skill";

/**
 * Study-centre learning hub. The learner's real, API-backed study plans
 * (`LearningPathway`, SC-3b) plus the daily coach that runs on top of them
 * (diagnostic → ranked gaps → SM-2 review queue, SC-3d). Everything here
 * is server data — no invented progress, which is the trap the mocked
 * revision queue fell into before SC-3b removed it. Study-centre-only:
 * every read hits the vertical-gated `/api/learning/*` surface.
 */
export function LearningHub({
  ziggyAge = "grown",
  centreName,
  initialAction = null,
  onInitialActionConsumed,
}: {
  ziggyAge?: ZiggyAge;
  centreName?: string;
  initialAction?: LearnHubAction | null;
  onInitialActionConsumed?: () => void;
}) {
  const { user } = useSession();
  const qc = useQueryClient();
  const reduce = useReducedMotion();
  const firstName = user?.name?.trim().split(" ")[0] || null;
  const [view, setView] = React.useState<View>("hub");
  const [resumePathway, setResumePathway] = React.useState<Pathway | null>(null);
  const [session, setSession] = React.useState<DiagnosticSession | null>(null);
  const [practiceSession, setPracticeSession] = React.useState<PracticeTestSession | null>(null);
  const [skillSession, setSkillSession] = React.useState<SkillAssessmentSession | null>(null);
  const [scrollToRoadmap, setScrollToRoadmap] = React.useState(false);
  const roadmapSectionRef = React.useRef<HTMLDivElement>(null);
  const intakeJustCompletedRef = React.useRef(false);
  const [roadmapPlan, setRoadmapPlan] = React.useState<Pathway | null>(null);
  const [celebrateLeafId, setCelebrateLeafId] = React.useState<string | null>(null);
  /** Roadmap step whose qualify quiz is currently being generated. */
  const [qualifyingLeafId, setQualifyingLeafId] = React.useState<string | null>(null);
  const [qualifyFailed, setQualifyFailed] = React.useState(false);
  const startDiagnosticMut = useStartDiagnostic();

  const enrollmentQuery = useMyEnrollment();
  const learningEnabled =
    !enrollmentQuery.isLoading &&
    !enrollmentQuery.isError &&
    !enrollmentQuery.data?.needs_app_payment;

  const pathwaysQuery = usePathways(undefined, learningEnabled);
  const pathways = React.useMemo(
    () => pathwaysQuery.data?.items ?? [],
    [pathwaysQuery.data?.items],
  );
  const activeCount = pathwaysQuery.data?.active_count ?? 0;
  const activePlans = pathways.filter((p) => p.status === "active" && p.onboarding_complete);
  const incompletePathway = React.useMemo(
    () => pathways.find((p) => !p.onboarding_complete) ?? null,
    [pathways],
  );
  const hasActivePlan = activePlans.length > 0;
  const { selectedPlan, selectPlan } = useSelectedPathway(activePlans);
  const { strings } = useLearnLocale(learningEnabled);
  const needsIntake =
    !pathways.some((p) => p.onboarding_complete) &&
    (enrollmentQuery.data?.needs_intake ?? pathways.length === 0);

  const refreshCoach = React.useCallback(() => {
    void qc.invalidateQueries({ queryKey: COACH_KEY.root });
  }, [qc]);

  const openIntake = React.useCallback(
    (resume: Pathway | null = incompletePathway) => {
      setResumePathway(resume);
      setView("intake");
    },
    [incompletePathway],
  );

  React.useEffect(() => {
    if (intakeJustCompletedRef.current) return;
    if (enrollmentQuery.isLoading || enrollmentQuery.isError) return;
    if (enrollmentQuery.data?.needs_app_payment) return;
    if (needsIntake && view === "hub") {
      openIntake(incompletePathway);
    }
  }, [
    enrollmentQuery.isLoading,
    enrollmentQuery.isError,
    enrollmentQuery.data?.needs_app_payment,
    needsIntake,
    view,
    incompletePathway,
    openIntake,
  ]);

  // The coach only exists once there's a plan to coach, so both queries
  // stay parked until then rather than 200-ing with empty counters.
  const pleasurePlan = selectedPlan ? isReadForPleasurePlan(selectedPlan.purpose) : false;
  const summaryQuery = useCoachSummary(
    hasActivePlan && learningEnabled && !pleasurePlan,
    selectedPlan?.id ?? null,
  );
  const dueQuery = useDueCards(
    hasActivePlan && learningEnabled && !pleasurePlan,
    selectedPlan?.id ?? null,
  );

  const planDueQueries = useQueries({
    queries: activePlans.map((plan) => ({
      queryKey: [...COACH_KEY.due, plan.id],
      queryFn: () => coachApi.dueCards(1, plan.id),
      enabled: activePlans.length > 1 && learningEnabled && !isReadForPleasurePlan(plan.purpose),
      staleTime: 60_000,
    })),
  });
  const dueByPlanId = React.useMemo(() => {
    const map: Record<string, number> = {};
    activePlans.forEach((plan, i) => {
      map[plan.id] = planDueQueries[i]?.data?.total_due ?? 0;
    });
    return map;
  }, [activePlans, planDueQueries]);

  React.useEffect(() => {
    if (initialAction !== "roadmap" || !selectedPlan || pleasurePlan) return;
    setRoadmapPlan(selectedPlan);
    setView("roadmap");
    onInitialActionConsumed?.();
  }, [initialAction, selectedPlan, pleasurePlan, onInitialActionConsumed]);

  React.useEffect(() => {
    if (!scrollToRoadmap || !roadmapSectionRef.current) return;
    roadmapSectionRef.current.scrollIntoView({
      behavior: reduce ? "auto" : "smooth",
      block: "start",
    });
    setScrollToRoadmap(false);
  }, [scrollToRoadmap, selectedPlan?.id, reduce]);

  if (enrollmentQuery.isLoading) {
    return (
      <div className="text-muted-foreground flex items-center justify-center gap-2 py-16 text-sm">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        {strings.loading}
      </div>
    );
  }

  if (enrollmentQuery.data?.needs_app_payment) {
    const paidUntil = enrollmentQuery.data.app_access_paid_until;
    const isLapsed = Boolean(paidUntil && new Date(paidUntil).getTime() < Date.now());
    return (
      <AppPaywall
        monthlyPricePaise={enrollmentQuery.data.learner_app_monthly_price_paise ?? 0}
        centreName={centreName ?? strings.yourCentreFallback}
        pricingMode={enrollmentQuery.data.learner_app_pricing_mode}
        isLapsed={isLapsed}
        lapsedPaidUntil={paidUntil}
        onAccessGranted={async () => {
          await qc.invalidateQueries({ queryKey: MY_ENROLLMENT_KEY });
          await enrollmentQuery.refetch();
        }}
      />
    );
  }

  if (view === "intake") {
    return (
      <div className="lg:mx-auto lg:max-w-2xl">
        <IntakeWizard
          ziggyAge={ziggyAge}
          resumePathway={resumePathway}
          onClose={() => {
            setResumePathway(null);
            setView("hub");
          }}
          onCreated={async (pathway) => {
            intakeJustCompletedRef.current = true;
            setResumePathway(null);
            selectPlan(pathway.id);
            setRoadmapPlan(pathway);
            await qc.invalidateQueries({ queryKey: MY_ENROLLMENT_KEY });
            await enrollmentQuery.refetch();
            await pathwaysQuery.refetch();
            if (isReadForPleasurePlan(pathway.purpose)) {
              setView("hub");
            } else {
              setView("roadmap");
            }
            window.setTimeout(() => {
              intakeJustCompletedRef.current = false;
            }, 800);
          }}
        />
      </div>
    );
  }

  if (view === "diagnostic" && session) {
    return (
      <div className="lg:mx-auto lg:max-w-2xl">
        <DiagnosticRunner
          session={session}
          ziggyAge={ziggyAge}
          onClose={() => {
            const returnToRoadmap = Boolean(session.leaf_id);
            setSession(null);
            setView(returnToRoadmap ? "roadmap" : "hub");
          }}
          // Scoring is not the end of the session: the runner stays mounted so the
          // learner can read the answer key, the explanations, and the gap ranking
          // that the submit response just revealed. Leaving the view here would
          // unmount it mid-render and throw them back to the hub having learnt
          // nothing from the quiz. Navigation belongs to `onClose`, which the
          // results screen's own Done button calls.
          onFinished={(result) => {
            const returnToRoadmap = Boolean(session.leaf_id);
            if (returnToRoadmap && result.step_passed && session.leaf_id) {
              setCelebrateLeafId(session.leaf_id);
            }
            void qc.invalidateQueries({ queryKey: COACH_KEY.root });
            if (returnToRoadmap && roadmapPlan) {
              void qc.invalidateQueries({
                queryKey: ["learning", "course-navigation", roadmapPlan.id],
              });
            }
          }}
        />
      </div>
    );
  }

  if (view === "roadmap" && roadmapPlan) {
    return (
      <PlanRoadmapPage
        pathway={roadmapPlan}
        ziggyAge={ziggyAge}
        celebrateLeafId={celebrateLeafId}
        onCelebrateDone={() => setCelebrateLeafId(null)}
        onBack={() => {
          setView("hub");
        }}
        busyLeafId={qualifyingLeafId}
        qualifyFailed={qualifyFailed}
        onQualifyStep={async (leaf: CourseLeaf) => {
          // Generating the qualify quiz is a multi-second AI call. Guard against
          // a second tap and mark the step busy so the roadmap does not look dead.
          if (qualifyingLeafId) return;
          setQualifyingLeafId(leaf.id);
          setQualifyFailed(false);
          try {
            const stepSession = await startDiagnosticMut.mutateAsync({
              pathwayId: roadmapPlan.id,
              body: {
                leaf_id: leaf.id,
                question_count: leaf.min_questions_required,
              },
            });
            setSession(stepSession);
            setView("diagnostic");
          } catch {
            setQualifyFailed(true);
          } finally {
            setQualifyingLeafId(null);
          }
        }}
      />
    );
  }

  if (view === "practice" && practiceSession) {
    return (
      <div className="lg:mx-auto lg:max-w-2xl">
        <PracticeTestRunner
          session={practiceSession}
          ziggyAge={ziggyAge}
          onClose={() => {
            setPracticeSession(null);
            setView("hub");
          }}
        />
      </div>
    );
  }

  if (view === "skill" && skillSession) {
    return (
      <div className="lg:mx-auto lg:max-w-2xl">
        <SkillAssessmentRunner
          session={skillSession}
          onClose={() => {
            setSkillSession(null);
            setView("hub");
          }}
        />
      </div>
    );
  }

  if (view === "review") {
    return (
      <div className="lg:mx-auto lg:max-w-2xl">
        <ReviewSession
          cards={dueQuery.data?.items ?? []}
          ziggyAge={ziggyAge}
          onClose={() => {
            refreshCoach();
            setView("hub");
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5 lg:mx-auto lg:max-w-4xl lg:space-y-6">
      {pathwaysQuery.isLoading ? (
        <PlansSkeleton />
      ) : pathwaysQuery.isError && isModuleDisabledError(pathwaysQuery.error) ? (
        <ModuleDisabledState strings={strings} />
      ) : pathwaysQuery.isError ? (
        <ErrorState strings={strings} onRetry={() => pathwaysQuery.refetch()} />
      ) : needsIntake ? (
        <EmptyState
          ziggyAge={ziggyAge}
          strings={strings}
          onStart={() => openIntake(incompletePathway)}
        />
      ) : (
        <>
          <Reveal>
            <LearnPageHeader ziggyAge={ziggyAge} strings={strings} />
          </Reveal>

          {hasActivePlan && selectedPlan && activePlans.length > 1 && (
            <Reveal index={1}>
              <SectionCard className="mb-1">
                <PlanPicker
                  plans={activePlans}
                  selectedId={selectedPlan.id}
                  onSelect={selectPlan}
                  label={strings.choosePlan}
                  dueByPlanId={dueByPlanId}
                  formatDueLabel={strings.topicsDueOnPlan}
                  formatSwitchLabel={strings.switchPlanAria}
                />
              </SectionCard>
            </Reveal>
          )}
          {hasActivePlan && selectedPlan && (
            <Reveal index={1}>
              <DailyCoach
                ziggyAge={ziggyAge}
                selectedPlan={selectedPlan}
                plans={activePlans}
                dueNow={summaryQuery.data?.due_now ?? 0}
                reviewedToday={summaryQuery.data?.reviewed_today ?? 0}
                readsLoggedToday={summaryQuery.data?.reads_logged_today ?? 0}
                needsDiagnostic={summaryQuery.data?.pathways_needing_diagnostic ?? []}
                loading={summaryQuery.isLoading}
                initialAction={initialAction}
                onInitialActionConsumed={onInitialActionConsumed}
                onStarted={(s) => {
                  setSession(s);
                  setView("diagnostic");
                }}
                onPracticeStarted={(s) => {
                  setPracticeSession(s);
                  setView("practice");
                }}
                onReview={() => setView("review")}
              />
            </Reveal>
          )}

          {hasActivePlan && selectedPlan && !isReadForPleasurePlan(selectedPlan.purpose) && (
            <Reveal index={2}>
              <TrackRecordPanel pathway={selectedPlan} strings={strings} />
            </Reveal>
          )}

          {hasActivePlan && selectedPlan && !isReadForPleasurePlan(selectedPlan.purpose) && (
            <>
              <Reveal index={3}>
                <SkillAssessmentPanel
                  pathway={selectedPlan}
                  onStarted={(session) => {
                    setSkillSession(session);
                    setView("skill");
                  }}
                />
              </Reveal>
            </>
          )}

          <Reveal index={4}>
            <SectionHeader
              title={activeCount > 0 ? strings.activePlansTitle(activeCount) : strings.yourPlans}
              action={strings.newPlan}
              onAction={() => openIntake(incompletePathway)}
            />
            <div className="space-y-3 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0">
              {pathways.map((p, i) => (
                <PathwayProgressCard
                  key={p.id}
                  pathway={p}
                  accent={i % 2 === 0 ? "violet" : "green"}
                  onFinishSetup={(pathway) => {
                    intakeJustCompletedRef.current = false;
                    setResumePathway(pathway);
                    setView("intake");
                  }}
                  onOpenRoadmap={(pathway) => {
                    setRoadmapPlan(pathway);
                    setView("roadmap");
                  }}
                />
              ))}
            </div>
          </Reveal>

          <Reveal index={5}>
            <MotivationBanner
              message={strings.motivationBanner(firstName ?? strings.learnerFallbackName)}
            />
          </Reveal>
        </>
      )}
    </div>
  );
}

/**
 * The daily coach card: today's queue if there is one, otherwise the
 * diagnostic that creates it.
 *
 * Starting a diagnostic is a deliberate tap, never automatic — it costs an
 * AI call. A second tap while one is open is harmless: the BE returns the
 * open session instead of generating another quiz.
 */
function DailyCoach({
  ziggyAge,
  selectedPlan,
  plans,
  dueNow,
  reviewedToday,
  readsLoggedToday,
  needsDiagnostic,
  loading,
  initialAction,
  onInitialActionConsumed,
  onStarted,
  onPracticeStarted,
  onReview,
}: {
  ziggyAge: ZiggyAge;
  selectedPlan: Pathway;
  plans: Pathway[];
  dueNow: number;
  reviewedToday: number;
  readsLoggedToday: number;
  needsDiagnostic: string[];
  loading: boolean;
  initialAction?: LearnHubAction | null;
  onInitialActionConsumed?: () => void;
  onStarted: (session: DiagnosticSession) => void;
  onPracticeStarted: (session: PracticeTestSession) => void;
  onReview: () => void;
}) {
  const { strings } = useLearnLocale();
  const prefsQuery = useLearningPreferences();
  const prefs = prefsQuery.data;
  const maxQ = prefs?.max_questions_per_quiz ?? 50;
  const defaultDiagnostic = prefs?.default_diagnostic_question_count ?? 8;

  const startMut = useStartDiagnostic();
  const practiceMut = useStartPracticeTest();
  const [error, setError] = React.useState<string | null>(null);
  const [readLogOpen, setReadLogOpen] = React.useState(false);
  const [practiceOpen, setPracticeOpen] = React.useState(false);
  const [reportOpen, setReportOpen] = React.useState(false);
  const [diagnosticOpen, setDiagnosticOpen] = React.useState(false);
  const [diagnosticCount, setDiagnosticCount] = React.useState(defaultDiagnostic);

  React.useEffect(() => {
    setDiagnosticCount(defaultDiagnostic);
  }, [defaultDiagnostic, selectedPlan.id]);

  const start = React.useCallback(
    async (pathwayId: string, questionCount?: number) => {
      setError(null);
      try {
        const count = clampQuestionCount(questionCount ?? diagnosticCount, 3, Math.min(100, maxQ));
        onStarted(
          await startMut.mutateAsync({
            pathwayId,
            body: { question_count: count },
          }),
        );
        setDiagnosticOpen(false);
      } catch (err) {
        setError(diagnosticError(err, strings));
      }
    },
    [diagnosticCount, maxQ, onStarted, startMut, strings],
  );

  const pending = plans.filter((p) => needsDiagnostic.includes(p.id));
  const target = selectedPlan;
  const pleasure = isReadForPleasurePlan(target.purpose);
  const pendingDiagnostic = !pleasure && pending.some((p) => p.id === target.id);

  React.useEffect(() => {
    if (!initialAction || !target || loading) return;
    switch (initialAction) {
      case "quiz":
        // Open the sheet as well as starting: it owns the "Building quiz…" and
        // error UI. Without it the promo card's busy state can be hidden behind
        // the review CTA, so the AI call would run with no feedback at all.
        setDiagnosticOpen(true);
        void start(target.id, defaultDiagnostic);
        break;
      case "practice":
        setPracticeOpen(true);
        break;
      case "reading":
        setReadLogOpen(true);
        break;
      case "progress":
        setReportOpen(true);
        break;
      case "roadmap":
        break;
    }
    if (initialAction !== "roadmap") {
      onInitialActionConsumed?.();
    }
  }, [initialAction, target, loading, onInitialActionConsumed, defaultDiagnostic, start]);

  // Read-for-pleasure plans have exactly one action, and the promo card above
  // already carries it — a second identical "Log reading" card stacked directly
  // beneath it was a duplicate CTA rendered as a quarter-width sliver.
  const quickActions: QuickActionItem[] = pleasure
    ? []
    : [
        {
          id: "report",
          label: strings.progressReport,
          description: strings.progressReportHint,
          icon: Sparkles,
          tint: "violet",
          onClick: () => setReportOpen(true),
        },
        {
          id: "practice",
          label: strings.practiceTest,
          description: strings.practiceTestHint,
          icon: BookOpen,
          tint: "orange",
          onClick: () => setPracticeOpen(true),
        },
        {
          id: "reading",
          label: strings.logReading,
          description: strings.logReadingHint,
          icon: PenLine,
          tint: "green",
          onClick: () => setReadLogOpen(true),
        },
        {
          id: "quiz",
          label: strings.takeQuiz,
          description: strings.takeQuizHint,
          icon: Brain,
          tint: "blue",
          onClick: () => setDiagnosticOpen(true),
        },
      ];

  return (
    <div className="space-y-4">
      <QuizPromoCard
        ziggyAge={ziggyAge}
        target={target}
        dueNow={pleasure ? 0 : dueNow}
        reviewedToday={pleasure ? 0 : reviewedToday}
        readsLoggedToday={readsLoggedToday}
        pendingDiagnostic={pendingDiagnostic}
        pleasureMode={pleasure}
        loading={loading}
        quizBusy={startMut.isPending}
        strings={strings}
        onReview={pleasure ? () => setReadLogOpen(true) : onReview}
        onTakeQuiz={() => (pleasure ? setReadLogOpen(true) : setDiagnosticOpen(true))}
        error={error}
      />

      {target && <QuickActionGrid items={quickActions} />}

      {target && (
        <>
          <DiagnosticStartSheet
            open={diagnosticOpen}
            onOpenChange={setDiagnosticOpen}
            pathway={target}
            busy={startMut.isPending}
            questionCount={diagnosticCount}
            onQuestionCountChange={setDiagnosticCount}
            maxQuestions={maxQ}
            strings={strings}
            error={error}
            onStart={() => void start(target.id, diagnosticCount)}
          />
          <ReadLogSheet
            open={readLogOpen}
            onOpenChange={setReadLogOpen}
            pathway={target}
            defaultSubject={target.subjects[0] ?? ""}
            strings={strings}
          />
          <PracticeTestSheet
            open={practiceOpen}
            onOpenChange={setPracticeOpen}
            pathway={target}
            busy={practiceMut.isPending}
            maxQuestions={maxQ}
            defaultCount={prefs?.default_practice_question_count ?? 8}
            strings={strings}
            onStart={async (body) => {
              setError(null);
              try {
                onPracticeStarted(await practiceMut.mutateAsync({ pathwayId: target.id, body }));
                setPracticeOpen(false);
              } catch (err) {
                setError(practiceError(err, strings));
              }
            }}
          />
          <ProgressReportSheet
            open={reportOpen}
            onOpenChange={setReportOpen}
            pathway={target}
            strings={strings}
          />
        </>
      )}
    </div>
  );
}

function TrackRecordPanel({
  pathway,
  strings,
}: {
  pathway: Pathway;
  strings: ReturnType<typeof useLearnLocale>["strings"];
}) {
  const { locale } = useLearnLocale();
  const trackQuery = useTrackRecord(pathway.id);
  const items = trackQuery.data?.items ?? [];
  const [detailEntry, setDetailEntry] = React.useState<TrackRecordEntry | null>(null);
  const [expanded, setExpanded] = React.useState(false);
  const previewLimit = 5;
  const hasMore = items.length > previewLimit;
  const visibleItems = expanded ? items : items.slice(0, previewLimit);

  return (
    <div>
      <SectionHeader
        title={strings.trackRecord}
        action={hasMore ? (expanded ? strings.showLess : strings.viewAll) : undefined}
        onAction={hasMore ? () => setExpanded((prev) => !prev) : undefined}
      />
      <SectionCard className="divide-border/60 divide-y p-0">
        {trackQuery.isLoading ? (
          <div className="space-y-2 p-4">
            <div className="bg-muted/60 h-10 animate-pulse rounded-lg" />
            <div className="bg-muted/60 h-10 animate-pulse rounded-lg" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-muted-foreground p-4 text-xs">{strings.trackRecordEmpty}</p>
        ) : (
          <ul>
            {visibleItems.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => setDetailEntry(entry)}
                  className="hover:bg-muted/30 flex w-full items-start justify-between gap-3 px-4 py-3.5 text-left transition-colors duration-150"
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="bg-primary/12 text-primary grid size-10 shrink-0 place-items-center rounded-xl">
                      <History className="size-4" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">{entry.title}</p>
                      {entry.summary && (
                        <p className="text-muted-foreground truncate text-xs">{entry.summary}</p>
                      )}
                    </div>
                  </div>
                  <time
                    dateTime={entry.occurred_at}
                    className="text-muted-foreground shrink-0 pt-0.5 text-[0.68rem] font-medium tabular-nums"
                  >
                    {formatActivityDate(entry.occurred_at, locale)}
                  </time>
                </button>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
      <TrackRecordDetailSheet
        entry={detailEntry}
        open={Boolean(detailEntry)}
        onOpenChange={(open) => {
          if (!open) setDetailEntry(null);
        }}
      />
    </div>
  );
}

function ProgressReportSheet({
  open,
  onOpenChange,
  pathway,
  strings,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pathway: Pathway;
  strings: ReturnType<typeof useLearnLocale>["strings"];
}) {
  const { locale } = useLearnLocale();
  const generateMut = useGenerateProgressReport();
  const reportsQuery = useProgressReports(pathway.id, open);
  const notificationsQuery = useNotifications(open);
  const markReadMut = useMarkNotificationRead();
  const [selected, setSelected] = React.useState<ProgressReport | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [announce, setAnnounce] = React.useState<string | null>(null);

  // The sheet stays mounted across plan switches, so a report generated for the
  // previous plan would otherwise keep rendering under the new plan's title.
  React.useEffect(() => {
    setError(null);
    setAnnounce(null);
    setSelected(null);
  }, [open, pathway.id]);

  async function generate() {
    setError(null);
    setAnnounce(null);
    try {
      const report = await generateMut.mutateAsync({ pathwayId: pathway.id, periodDays: 7 });
      setSelected(report);
      setAnnounce(strings.reportReadyAnnounce);
    } catch (err) {
      setError(reportError(err, strings));
    }
  }

  const latest = reportsQuery.data?.items[0] ?? null;
  const candidate = selected ?? latest;
  const viewing = candidate?.pathway_id === pathway.id ? candidate : null;
  const loadingReports = open && reportsQuery.isLoading && !viewing;

  /*
   * Clear the badge for the report the learner is actually looking at, and
   * only that one.
   *
   * Generating a report makes the server mint a *new* unread notification, so
   * the previous approach — snapshot the unread list, generate, then mark that
   * snapshot read — cleared notifications belonging to other plans while
   * leaving the one it had just created unread. The count never reached zero
   * and each generation silently consumed a different plan's notification.
   *
   * Matching on `related_id` also covers the case where the sheet opens
   * straight onto an already-generated report, with no generate tap at all.
   */
  const unreadForViewing = viewing
    ? (notificationsQuery.data?.items.find((n) => !n.read_at && n.related_id === viewing.id) ??
      null)
    : null;
  const markRead = markReadMut.mutate;

  // Marking read invalidates the notifications query, so a response that still
  // reports the notice as unread — a failed write, a read replica lagging —
  // would otherwise re-arm this effect and hammer the endpoint in a loop.
  const acknowledged = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    if (!open || !unreadForViewing) return;
    if (acknowledged.current.has(unreadForViewing.id)) return;
    acknowledged.current.add(unreadForViewing.id);
    // Fire-and-forget: a read receipt that fails must not surface as a report
    // error when the report itself is already on screen.
    markRead(unreadForViewing.id);
  }, [open, unreadForViewing, markRead]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <ResponsiveSheetContent>
        <SheetHeader>
          <SheetTitle>{strings.progressReportTitle}</SheetTitle>
          <SheetDescription>{strings.progressReportDescription(pathway.title)}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {unreadForViewing && (
            <div className="bg-primary/8 border-primary/20 flex items-start gap-2 rounded-lg border px-3 py-2">
              <Bell className="text-primary mt-0.5 size-4 shrink-0" />
              <p className="text-xs">{strings.progressReportNewUpdates(1)}</p>
            </div>
          )}

          {loadingReports ? (
            <div className="flex justify-center py-8">
              <Loader2
                className="text-muted-foreground size-5 animate-spin"
                aria-label={strings.loadingReports}
              />
            </div>
          ) : viewing ? (
            <div className="space-y-3">
              <p className="text-muted-foreground text-[0.68rem] uppercase tracking-wide">
                {strings.reportGeneratedOn(
                  formatActivityDate(viewing.generated_at, locale),
                  viewing.period_days,
                )}
              </p>
              <p className="text-sm leading-relaxed">{viewing.narrative}</p>
              {viewing.focus_areas.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide">
                    {strings.focusNext}
                  </p>
                  <ul className="mt-2 space-y-1">
                    {viewing.focus_areas.map((area) => (
                      <li key={area} className="text-muted-foreground text-sm">
                        • {area}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {viewing.encouragement && (
                <p className="text-joy-green text-sm font-medium">{viewing.encouragement}</p>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">{strings.progressReportEmpty}</p>
          )}

          {error && (
            <p role="alert" className="text-destructive text-center text-xs font-medium">
              {error}
            </p>
          )}

          <p aria-live="polite" className="sr-only">
            {announce ?? ""}
          </p>

          <Button
            className="w-full"
            disabled={generateMut.isPending}
            onClick={() => void generate()}
          >
            {generateMut.isPending ? (
              <>
                <Loader2 className="mr-1.5 size-4 animate-spin" /> {strings.writingReport}
              </>
            ) : viewing ? (
              strings.generateFreshReport
            ) : (
              strings.generateProgressReport
            )}
          </Button>
        </div>
      </ResponsiveSheetContent>
    </Sheet>
  );
}

function formatActivityDate(iso: string, locale: PortalLocale): string {
  const date = new Date(iso);
  return date.toLocaleDateString(intlLocale(locale), { month: "short", day: "numeric" });
}

function reportError(err: unknown, strings: ReturnType<typeof useLearnLocale>["strings"]): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return strings.reportError429;
    if (err.status === 503) return strings.reportError503;
  }
  return strings.reportErrorGeneric;
}

function ReadLogSheet({
  open,
  onOpenChange,
  pathway,
  defaultSubject,
  strings,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pathway: Pathway;
  defaultSubject: string;
  strings: ReturnType<typeof useLearnLocale>["strings"];
}) {
  const logMut = useLogReading();
  const navQuery = useCourseNavigation(pathway.id, pathway.onboarding_complete && open);
  const roadmapLeaves = navQuery.data?.has_roadmap ? navQuery.data.leaves : [];
  const pickableLeaves = roadmapLeaves.filter((leaf) => leaf.status !== "locked");
  const [selectedLeafId, setSelectedLeafId] = React.useState<string>("");
  const [subject, setSubject] = React.useState(defaultSubject);
  const [topic, setTopic] = React.useState("");
  const [minutes, setMinutes] = React.useState("");
  const [note, setNote] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setSelectedLeafId("");
      setSubject(defaultSubject);
      setTopic("");
      setMinutes("");
      setNote("");
      setError(null);
      setSaved(false);
    }
  }, [open, defaultSubject]);

  React.useEffect(() => {
    if (!selectedLeafId) return;
    const leaf = pickableLeaves.find((l) => l.id === selectedLeafId);
    if (leaf) {
      setSubject(leaf.subject);
      setTopic(leaf.title);
    }
  }, [selectedLeafId, pickableLeaves]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    const trimmedTopic = topic.trim();
    const trimmedSubject = subject.trim();
    if (!trimmedSubject || !trimmedTopic) {
      setError(strings.readLogSubjectTopicRequired);
      return;
    }
    const parsedMinutes = minutes.trim() ? Number(minutes) : undefined;
    if (parsedMinutes !== undefined && (!Number.isFinite(parsedMinutes) || parsedMinutes < 1)) {
      setError(strings.readLogMinutesInvalid);
      return;
    }
    try {
      await logMut.mutateAsync({
        pathwayId: pathway.id,
        body: {
          subject: trimmedSubject,
          topic: trimmedTopic,
          minutes_spent: parsedMinutes,
          note: note.trim() || undefined,
        },
      });
      setSaved(true);
      window.setTimeout(() => onOpenChange(false), 900);
    } catch {
      setError(strings.readLogSaveFailed);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <ResponsiveSheetContent>
        <SheetHeader>
          <SheetTitle>{strings.readLogTitle}</SheetTitle>
          <SheetDescription>{strings.readLogDescription(pathway.title)}</SheetDescription>
        </SheetHeader>

        <form onSubmit={(e) => void submit(e)} className="mt-4 space-y-4">
          <div className="border-border/60 bg-muted/30 rounded-xl border px-3 py-2.5">
            <p className="text-muted-foreground text-[0.65rem] font-semibold uppercase tracking-wide">
              {strings.loggingForPlan}
            </p>
            <p className="truncate text-sm font-medium">{pathway.title}</p>
          </div>

          {pickableLeaves.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="read-log-roadmap-step">{strings.roadmapStep}</Label>
              <Select value={selectedLeafId} onValueChange={setSelectedLeafId}>
                <SelectTrigger id="read-log-roadmap-step" className="w-full">
                  <SelectValue placeholder={strings.pickRoadmapStep} />
                </SelectTrigger>
                <SelectContent>
                  {pickableLeaves.map((leaf) => (
                    <SelectItem key={leaf.id} value={leaf.id}>
                      {leaf.subject} — {leaf.title}
                      {leaf.read_log_count > 0 ? ` (${leaf.read_log_count} reads)` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="read-log-subject">{strings.subject}</Label>
            <Input
              id="read-log-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={strings.skillSubjectPlaceholder}
              autoComplete="off"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="read-log-topic">{strings.readLogTopicLabel}</Label>
            <Input
              id="read-log-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={strings.readLogTopicPlaceholder}
              autoComplete="off"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="read-log-minutes">{strings.readLogMinutesOptional}</Label>
            <Input
              id="read-log-minutes"
              inputMode="numeric"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              placeholder={strings.readLogMinutesPlaceholder}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="read-log-note">{strings.readLogNoteOptional}</Label>
            <Input
              id="read-log-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={strings.readLogNotePlaceholder}
            />
          </div>

          {error && (
            <p role="alert" className="text-destructive text-center text-xs font-medium">
              {error}
            </p>
          )}
          {saved && (
            <p className="text-joy-green text-center text-xs font-medium">{strings.readLogSaved}</p>
          )}

          <Button type="submit" className="w-full" disabled={logMut.isPending || saved}>
            {logMut.isPending ? (
              <>
                <Loader2 className="mr-1.5 size-4 animate-spin" /> {strings.readLogSaving}
              </>
            ) : (
              strings.saveReadingLog
            )}
          </Button>
        </form>
      </ResponsiveSheetContent>
    </Sheet>
  );
}

/**
 * The two failures a learner can actually act on get their own message;
 * everything else is a generic retry rather than a leaked status code.
 * 429 is the per-day diagnostic cap, 503 is the AI provider being down.
 */
function diagnosticError(
  err: unknown,
  strings: ReturnType<typeof useLearnLocale>["strings"],
): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return strings.diagnosticError429;
    if (err.status === 503) return strings.diagnosticError503;
    if (err.status === 0) return strings.errorOffline;
  }
  return strings.diagnosticErrorGeneric;
}

function practiceError(
  err: unknown,
  strings: ReturnType<typeof useLearnLocale>["strings"],
): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return strings.practiceError429;
    if (err.status === 503) return strings.practiceError503;
  }
  return strings.practiceErrorGeneric;
}

function DiagnosticStartSheet({
  open,
  onOpenChange,
  pathway,
  busy,
  questionCount,
  onQuestionCountChange,
  maxQuestions,
  strings,
  error,
  onStart,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pathway: Pathway;
  busy: boolean;
  questionCount: number;
  onQuestionCountChange: (value: number) => void;
  maxQuestions: number;
  strings: ReturnType<typeof useLearnLocale>["strings"];
  error?: string | null;
  onStart: () => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <ResponsiveSheetContent>
        <SheetHeader>
          <SheetTitle>{strings.diagnosticTitle}</SheetTitle>
          <SheetDescription>
            {strings.diagnosticHint} — {pathway.title}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <QuestionCountPicker
            id="diagnostic-count"
            label={strings.questions}
            value={questionCount}
            min={3}
            max={Math.min(100, maxQuestions)}
            onChange={onQuestionCountChange}
          />
          {error && (
            <p role="alert" className="text-destructive text-center text-xs font-medium">
              {error}
            </p>
          )}
          <Button className="w-full" disabled={busy} onClick={onStart}>
            {busy ? (
              <>
                <Loader2 className="mr-1.5 size-4 animate-spin" /> {strings.buildingQuiz}
              </>
            ) : (
              strings.startDiagnostic
            )}
          </Button>
        </div>
      </ResponsiveSheetContent>
    </Sheet>
  );
}

function PracticeTestSheet({
  open,
  onOpenChange,
  pathway,
  busy,
  maxQuestions,
  defaultCount,
  strings,
  onStart,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pathway: Pathway;
  busy: boolean;
  maxQuestions: number;
  defaultCount: number;
  strings: ReturnType<typeof useLearnLocale>["strings"];
  onStart: (body: { topics: string[]; question_count: number }) => Promise<void>;
}) {
  const [topics, setTopics] = React.useState<string[]>([]);
  const [customTopic, setCustomTopic] = React.useState("");
  const [questionCount, setQuestionCount] = React.useState(defaultCount);
  const practiceMax = Math.min(20, maxQuestions);

  React.useEffect(() => {
    if (open) {
      setTopics(pathway.subjects.slice(0, 2));
      setCustomTopic("");
      setQuestionCount(defaultCount);
    }
  }, [open, pathway.subjects, defaultCount]);

  function toggleTopic(topic: string) {
    setTopics((prev) =>
      prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic],
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <ResponsiveSheetContent>
        <SheetHeader>
          <SheetTitle>{strings.practiceTitle}</SheetTitle>
          <SheetDescription>{strings.practicePickTopicsHint(pathway.title)}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{strings.practiceTopicsLabel}</legend>
            <div className="flex flex-wrap gap-2">
              {pathway.subjects.map((s) => (
                <button
                  key={s}
                  type="button"
                  aria-pressed={topics.includes(s)}
                  onClick={() => toggleTopic(s)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-semibold transition-colors duration-150",
                    topics.includes(s)
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border/60 text-muted-foreground",
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={customTopic}
                onChange={(e) => setCustomTopic(e.target.value)}
                placeholder={strings.addSubtopicPlaceholder}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const t = customTopic.trim();
                  if (t && !topics.includes(t)) setTopics((prev) => [...prev, t]);
                  setCustomTopic("");
                }}
              >
                {strings.add}
              </Button>
            </div>
          </fieldset>

          <QuestionCountPicker
            id="practice-count"
            label={strings.questions}
            value={questionCount}
            min={3}
            max={practiceMax}
            onChange={setQuestionCount}
          />

          <Button
            className="w-full"
            disabled={busy || topics.length === 0}
            onClick={() =>
              void onStart({
                topics,
                question_count: clampQuestionCount(questionCount, 3, practiceMax),
              })
            }
          >
            {busy ? (
              <>
                <Loader2 className="mr-1.5 size-4 animate-spin" /> {strings.buildingQuizButton}
              </>
            ) : (
              strings.startPractice
            )}
          </Button>
        </div>
      </ResponsiveSheetContent>
    </Sheet>
  );
}

/* ─────────────────────────── empty / loading ─────────────────────────── */

function EmptyState({
  ziggyAge,
  strings,
  onStart,
}: {
  ziggyAge: ZiggyAge;
  strings: ReturnType<typeof useLearnLocale>["strings"];
  onStart: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: EASE_OUT }}
      className="flex flex-col items-center gap-4 py-6 text-center"
    >
      <Ziggy pose="cheerful" age={ziggyAge} size={140} priority />
      <div>
        <p className="font-display text-xl font-semibold">{strings.buildStudyPlanTitle}</p>
        <p className="text-muted-foreground mx-auto mt-1 max-w-xs text-sm">
          {strings.buildStudyPlanHint}
        </p>
      </div>
      <button
        type="button"
        onClick={onStart}
        className="bg-primary text-primary-foreground flex items-center gap-1.5 rounded-xl px-6 py-3 text-sm font-semibold shadow-[var(--shadow-sm)] transition-transform duration-150 ease-out active:scale-[0.98]"
      >
        <Sparkles className="size-4" /> {strings.startMyPlan}
      </button>
    </motion.div>
  );
}

function PlansSkeleton() {
  return (
    <div className="space-y-3">
      <div className="bg-muted/60 h-6 w-40 animate-pulse rounded-lg" />
      {[0, 1].map((i) => (
        <div
          key={i}
          className="border-border/60 bg-card space-y-3 rounded-[1.25rem] border p-4 shadow-[var(--shadow-card)]"
        >
          <div className="flex items-center gap-3">
            <div className="bg-muted/60 size-10 animate-pulse rounded-xl" />
            <div className="flex-1 space-y-2">
              <div className="bg-muted/60 h-4 w-2/3 animate-pulse rounded" />
              <div className="bg-muted/60 h-3 w-1/3 animate-pulse rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ModuleDisabledState({
  strings,
}: {
  strings: ReturnType<typeof useLearnLocale>["strings"];
}) {
  return (
    <SectionCard className="flex flex-col items-center gap-3 py-8 text-center">
      <p className="font-display text-lg font-semibold">{strings.moduleDisabledTitle}</p>
      <p className="text-muted-foreground max-w-sm text-sm">{strings.moduleDisabledBody}</p>
    </SectionCard>
  );
}

function ErrorState({
  strings,
  onRetry,
}: {
  strings: ReturnType<typeof useLearnLocale>["strings"];
  onRetry: () => void;
}) {
  return (
    <SectionCard className="flex flex-col items-center gap-3 py-8 text-center">
      <p className="font-display text-lg font-semibold">{strings.plansLoadFailedTitle}</p>
      <p className="text-muted-foreground max-w-xs text-sm">{strings.plansLoadFailedBody}</p>
      <button
        type="button"
        onClick={onRetry}
        className="bg-primary text-primary-foreground mt-1 flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-sm font-semibold transition-transform duration-150 ease-out active:scale-[0.98]"
      >
        <RotateCcw className="size-4" /> {strings.retry}
      </button>
    </SectionCard>
  );
}
